import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';

import { IconClose, IconRefresh } from 'svgIcons/MotionPortalIcons';

import { GeneralFilterEditComponent } from './GeneralFilterEdit';
import GeneralFilterViewComponent from './GeneralFilterViewComponent';
import { GeneralButton, InfoButtonWithOnlineHelp } from 'sharedComponents';

import useValidateFilterState from './useValidateFilterState';
import useLoadDefaultFilters from './useLoadDefaultFilters';

import './style.scss';
import useLoadingSummary from 'sharedComponents/WithLoadingStatus/useLoadingSummary';
import { apiCallStatus } from 'helpers/constants';
import { setBreadcrumbStorage } from 'helpers/breadcrumbHelper';
import { refreshSelectedFilters, clearStore } from '../../PowertrainConditionMonitoring/ConditionIndicesOverviewV2/actions';
import { updateFleetOverviewInitialLoad, updateRefreshCacheFlag } from '../.././PowertrainFleets/FleetOverview/actions';
import { setFleetDashboardInitialLoadAction, setDashboardRefreshAnalytics } from '../../PowertrainFleets/FleetsDashboard/actions';
import { routes } from 'routes';

const GeneralFilterComponent = (props) => {

    const { t: translate } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const {
        organizationsLoadingStatus,
        sitesLoadingStatus,
        assetGroupsLoadingStatus,
        contextLoadingStatus
    } = props.model.loadingData;
    const isAdmin = props.model.isAdmin;

    const [isEditMode, setEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedOrg, setSelectedOrg] = useState(props.model.selectedFilters?.organizations?.[0] || {});
    const isVisibilityAdvancedFilter = false;

    const [isDirtyPage, setIsDirtyPage] = useState(false);
    const originalFiltersRef = useRef(props.model.selectedFilters);

    useEffect(() => {
        if (!isDirtyPage) {
            setSelectedOrg(props.model.selectedFilters?.organizations?.[0] || {});
        }
    }, [props.model.selectedFilters?.organizations]);

    // const enterEditMode = () => {
    //     originalFiltersRef.current = props.model.selectedFilters;
    //     setSelectedOrg(props.model.selectedFilters?.organizations?.[0] || {});
    //     setEditMode(true);
    // };

    useLoadingSummary((summary) => {
        setIsLoading(!summary || summary === apiCallStatus.LOADING);
    }, [organizationsLoadingStatus, sitesLoadingStatus, assetGroupsLoadingStatus, contextLoadingStatus]);

    useLoadDefaultFilters(props);
    useValidateFilterState(props);

    const onHide = () => {
        if (isEditMode && isDirtyPage) {
            props.actions.setSelectedFilters(originalFiltersRef.current);
            setSelectedOrg(originalFiltersRef.current?.organizations?.[0] || {});
        }
        setEditMode(false);
        setIsLoading(false);
        props.onHide();
    };

    const fleetOverviewInitialLoad = () => {
        dispatch(updateRefreshCacheFlag(true, true)); /* SETTING REFRESH CACHE AND CIAM SYNC FLAG TRUE ON GENERAL FILTER APPLY */
        if (props.model?.fleetUserType?.isLSU) {
            if (props.model?.fleetUserType?.isREC) {
                return;
            }
            dispatch(updateFleetOverviewInitialLoad());
        }
    };

    const onSave = (isFromSync = false) => {
        const isNonPowertrainOrganization = selectedOrg?.isPowertrainOrganization === false;
        setEditMode(false);
        setIsDirtyPage(false);
        props.actions.clearStore();
        props.actions?.updateRefreshDataFlag();
        dispatch(refreshSelectedFilters());
        props.actions?.saveFiltersWithSync();
        props.onHide();
        // Redirect to Tools route if the selected organization is a CIAM organization
        if (isNonPowertrainOrganization) {
            history.push({
                pathname: routes.Tools
            });
            return;
        }
        if (!isAdmin && !isFromSync) {
            props.actions.saveFilters();
        }
        history.push({
            pathname: history.location.pathname
        });
        setBreadcrumbStorage({});
        props.actions?.updateLastSelectedOrganization(selectedOrg);
        // When REC applies filters (Apply or Apply & Sync), refresh dashboard analytics for all fleets
        if (props.model?.fleetUserType?.isREC && typeof setDashboardRefreshAnalytics === 'function') {
            dispatch(setDashboardRefreshAnalytics(true));
        }
        fleetOverviewInitialLoad();
    };

    const onRequestCIAMSync = () => {
        if (isEditMode) {
            onSave();
            props?.actions?.setCIAMSyncAndRefreshFlags(true);
        } else {
            props?.actions?.launchCIAMSyncForAdmin(
                props?.model?.selectedFilters?.organizations,
                props?.model?.selectedFilters?.countries,
                true
            );
            // For REC users, trigger dashboard analytics refresh for all fleets as part of CIAM sync
            if (props.model?.fleetUserType?.isREC && typeof setDashboardRefreshAnalytics === 'function') {
                dispatch(setDashboardRefreshAnalytics(true));
            }
            fleetOverviewInitialLoad();
            props.actions?.updateRefreshDataFlag();
        }
        dispatch(setFleetDashboardInitialLoadAction(false)); /* REFRESH FLEET DASHBOARD WITH SYNC CIAM */
        onHide();
    };
    const renderSyncButton = (isEdit) => {
        return (
            <div>
                <GeneralButton
                    type='normal'
                    icon={IconRefresh({ width: '14px', height: '14px' })}
                    text={isEdit ? translate('ABB.Powertrain.Frontend.applySyncFromCIAM') : translate('ABB.Powertrain.Frontend.syncFromCIAM')}
                    onClick={onRequestCIAMSync}
                />
                <InfoButtonWithOnlineHelp infoText={translate('ABB.Powertrain.Frontend.syncFromCIAMInfo')} withoutLink={true} />
            </div>
        );
    };

    return <Modal
        show={props.show}
        aria-labelledby='general-filter-modal-title'
        id='general-filter-modal'
        className='general-filter-modal-container'
        backdropClassName='general-filter-modal-backdrop'
        onHide={onHide}
        backdrop='static'
    >
        <Modal.Header>
            <div className='modal-title' id='general-filter-modal-title'>
                <span className='title-text'>{translate('ABB.Powertrain.Frontend.organizationFilter')}</span>
                <InfoButtonWithOnlineHelp infoText={translate('ABB.Powertrain.Frontend.generalFilterInfo')} />
            </div>
            <div className='general-filter-collapse-btn' onClick={onHide} onKeyDown={onHide} role='modal-hide-icon'>
                <IconClose color='black' width={18} height={18} />
            </div>
        </Modal.Header>

        <Modal.Body>
            <GeneralFilterViewComponent
                loadingData={props.model.loadingData}
                selectedFilters={props.model.selectedFilters}
                searchAsset={props.actions.searchAssetRequest}
                onHide={onHide}
                assets={props.model.assets}
                userCapabilities={props.model?.userCapabilities}
                clearConditionOverviewStore={clearStore}
                isVisibilityAdvancedFilter={isVisibilityAdvancedFilter}
            />
            <GeneralFilterEditComponent
                selectedFilters={props.model.selectedFilters}
                setIsLoading={setIsLoading}
                setIsDirtyPage={setIsDirtyPage}
                setSelectedOrg={setSelectedOrg}
                selectedOrg={selectedOrg}
                isVisibilityAdvancedFilter={isVisibilityAdvancedFilter}
            />
        </Modal.Body>
        <Modal.Footer {...(isAdmin ? { className: 'footer_container' } : {})} >
            {isAdmin ? renderSyncButton(isEditMode) : null}
            {/* {isEditMode ? */}
            <div>
                {/* <GeneralButton type='discreet' text={translate('ABB.Powertrain.Frontend.generalFilterCancelButton')} disabled={isLoading} onClick={() => {
                        if (isDirtyPage) {
                            props.actions.setSelectedFilters(originalFiltersRef.current);
                            setSelectedOrg(originalFiltersRef.current?.organizations?.[0] || {});
                        }
                        setEditMode(false);
                    }} /> */}
                <GeneralButton type='primary' text={translate('ABB.Powertrain.Frontend.generalFilterApplyButton')} disabled={isLoading} onClick={() => { onSave(false); }} />
            </div>
            {/* : <div>
                    <GeneralButton type='discreet' text={translate('ABB.Powertrain.Frontend.generalFilterCloseButton')} onClick={onHide} />
                    <GeneralButton type='primary' text={translate('ABB.Powertrain.Frontend.generalFilterEditButton')} disabled={isLoading} onClick={enterEditMode} />
                </div> */}
            {/* } */}
        </Modal.Footer>
    </Modal>;
};

GeneralFilterComponent.propTypes = {
    actions: PropTypes.shape({
        saveFilters: PropTypes.func.isRequired,
        clearStore: PropTypes.func.isRequired,
        saveFiltersWithSync: PropTypes.func.isRequired,
        updateRefreshDataFlag: PropTypes.func.isRequired,
        setCIAMSyncAndRefreshFlags: PropTypes.func.isRequired,
        launchCIAMSyncForAdmin: PropTypes.func.isRequired,
        searchAssetRequest: PropTypes.func.isRequired,
        setSelectedFilters: PropTypes.func.isRequired,
        updateLastSelectedOrganization: PropTypes.func.isRequired
    }).isRequired,
    model: PropTypes.shape({
        loadingData: PropTypes.shape({
            organizationsLoadingStatus: PropTypes.string,
            sitesLoadingStatus: PropTypes.string,
            assetGroupsLoadingStatus: PropTypes.string,
            contextLoadingStatus: PropTypes.string
        }).isRequired,
        selectedFilters: PropTypes.shape({
            organizations: PropTypes.array.isRequired,
            countries: PropTypes.array.isRequired,
            sites: PropTypes.array.isRequired,
            assetGroups: PropTypes.array.isRequired
        }).isRequired,
        assets: PropTypes.arrayOf(PropTypes.object).isRequired,
        userCapabilities: PropTypes.arrayOf(PropTypes.string),
        isAdmin: PropTypes.bool.isRequired,
        fleetUserType: PropTypes.shape({
            isREC: PropTypes.bool,
            isLSU: PropTypes.bool
        })
    }).isRequired,
    onHide: PropTypes.func.isRequired,
    show: PropTypes.bool.isRequired
};

export default GeneralFilterComponent;
